import React, { createContext, useContext, useMemo } from 'react';

import { useStepper } from '../../common/hooks/useStepper';
import { STEPS_COUNT } from './constants';
import useDuplicateAds from './hooks/useDuplicateAds';
import type { DuplicateAdsContextType, DuplicateAdsStepperProviderProps } from './types';

const DuplicateAdsStepperContext = createContext({} as DuplicateAdsContextType);

export const useDuplicateAdsStepperContext = (): DuplicateAdsContextType => useContext(DuplicateAdsStepperContext);

const DuplicateAdsStepperProvider = ({
  children,
  onCloseStepper,
  originalDuplicatedAds,
  preSelectedLineItems,
}: DuplicateAdsStepperProviderProps): React.JSX.Element => {
  const { step, goToNextStep, goToPrevStep } = useStepper({ stepsCount: STEPS_COUNT, onComplete: onCloseStepper });
  const {
    lineItems,
    replaceLineItems,
    ads,
    setAds,
    updateLineItemAdName,
    handleUpdateLineItemsSelection,
    handleRemoveLineItem,
    adType,
    isSelectedSectionsChanged,
    setIsSelectedSectionsChanged,
    lastAppliedSectionsAdName,
    setLastAppliedSectionsAdName,
    getLineItemsWithInitiallyDeselected,
    getSelectedLineItems,
  } = useDuplicateAds({
    preSelectedLineItems,
    originalDuplicatedAds,
  });

  const value = useMemo(() => {
    return {
      lineItems,
      replaceLineItems,
      ads,
      setAds,
      updateLineItemAdName,
      onCloseStepper,
      step,
      goToNextStep,
      goToPrevStep,
      originalDuplicatedAds,
      handleRemoveLineItem,
      handleUpdateLineItemsSelection,
      adType,
      isSelectedSectionsChanged,
      setIsSelectedSectionsChanged,
      lastAppliedSectionsAdName,
      setLastAppliedSectionsAdName,
      getLineItemsWithInitiallyDeselected,
      getSelectedLineItems,
    };
  }, [
    lineItems,
    replaceLineItems,
    ads,
    setAds,
    updateLineItemAdName,
    onCloseStepper,
    step,
    goToNextStep,
    goToPrevStep,
    originalDuplicatedAds,
    handleRemoveLineItem,
    handleUpdateLineItemsSelection,
    adType,
    isSelectedSectionsChanged,
    setIsSelectedSectionsChanged,
    lastAppliedSectionsAdName,
    setLastAppliedSectionsAdName,
    getLineItemsWithInitiallyDeselected,
    getSelectedLineItems,
  ]);

  return <DuplicateAdsStepperContext.Provider value={value}>{children}</DuplicateAdsStepperContext.Provider>;
};

export default DuplicateAdsStepperProvider;
